import {
  Container,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Card,
  Stack,
  Typography,
  Dialog,
  Tooltip,
} from '@mui/material';
import { navigate } from 'gatsby';
import React, { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import Icon from '@components/Icon/Icon';
import NullStateDiv from '@components/NullStateDiv';

import { ASSESSMENTS_DHA, ASSESSMENTS_OTHER } from '@constants/routes';

import { useGetAppointments } from '@hooks/api/appointment';
import { useListAssessments } from '@hooks/api/assessment';
import { useSession } from '@hooks/useSession';

import { formatDate, formatName } from '@utils/format';

import PatientDialog from '@views/Dashboard/Assessments/PatientDialog';

import { AssessmentType } from 'generated/types';
import { useGetUserPlanQuery } from 'generated/user.generated';

import { Breakpoint } from '../../theme/breakpoints';

const messages = defineMessages({
  select: {
    id: 'assessmentsMain.select',
    defaultMessage: 'Select an assessment',
  },
  bha: {
    id: 'assessmentsMain.bha',
    defaultMessage:
      'Digital Health {count, plural, one {Assessment} other {Assessments}}',
  },
  noAssessments: {
    id: 'assessmentsMain.noAssessments',
    defaultMessage:
      'An assessment has not been performed or results have not been processed.',
  },
  startAssessment: {
    id: 'assessment.startAssessment',
    defaultMessage: 'Start Assessment',
  },
  dhaCredit: {
    id: 'assessment.dhaCredit',
    defaultMessage:
      'All Digital Health Assessments (DHA) credits have been used for this {interval}',
  },
});

const secondaryAction = (
  <Icon color="text.primary" name="chevron-down" rotation={-90} type="fas" />
);

interface AssessmentMainProps {
  assessmentType: string;
}

function AssessmentsMain({ assessmentType }: AssessmentMainProps) {
  const { formatMessage } = useIntl();

  const {
    state: { activeProfile, isPractitioner },
  } = useSession();

  const [isAssessmentOpen, setIsAssessmentOpen] = useState(false);

  const { data: assessmentsData = [], loading: assessmentsLoading } =
    useListAssessments(
      {
        type: AssessmentType.BHA,
      },
      assessmentType === 'other'
    );
  const { data: appointmentsData = [], loading: appointmentsLoading } =
    useGetAppointments(
      {
        serviceType: '252',
      },
      assessmentType !== 'other'
    );
  const { data: planData } = useGetUserPlanQuery({
    variables: { input: { user_id: activeProfile?.user_id ?? '' } },
    skip: !activeProfile?.user_id,
  });
  const dhaCredit = planData?.getUserRecord.plan?.credits.find(
    (c) => c.id === 'dha'
  );
  const loading = assessmentsLoading || appointmentsLoading;

  const mappedAssessments = assessmentsData.map(
    ({ assessment_id, completed_at }) => ({
      id: assessment_id,
      date: completed_at,
      utc: true,
      title: formatMessage(messages.bha, { count: 1 }),
    })
  );
  const mappedAppointments = appointmentsData.map(
    ({ id, visitDate, visitReason }) => ({
      id: id ?? '',
      date: visitDate,
      utc: true,
      title: visitReason ?? '',
    })
  );
  const data = mappedAssessments.concat(mappedAppointments);

  const activePatientData = {
    user_id: activeProfile?.user_id,
    vitall_patient_id: activeProfile?.vitall_patient_id,
    patient_id: activeProfile?.patient_id,
    //Based on patient plan DHA credit availability
    disabled: !dhaCredit?.remaining || dhaCredit.remaining === 0,
    first_name: activeProfile?.first_name,
    last_name: activeProfile?.last_name,
  };

  const handleCloseAssessment = () => {
    setIsAssessmentOpen(false);
  };

  const handleStartAssessment = () => {
    setIsAssessmentOpen(true);
  };

  let route: string;
  switch (assessmentType) {
    case 'other':
      route = ASSESSMENTS_OTHER;
      break;
    case 'dha':
    default:
      route = ASSESSMENTS_DHA;
      break;
  }

  return (
    <>
      <Container maxWidth="sm" sx={{ py: 2, textAlign: 'center' }}>
        <Stack spacing={10} mb={2} alignItems="flex-end">
          {isPractitioner && (
            <Tooltip
              title={
                activePatientData.disabled
                  ? formatMessage(messages.dhaCredit, {
                      interval: dhaCredit?.interval,
                    })
                  : ''
              }
            >
              <span>
                <Button
                  disabled={activePatientData.disabled}
                  onClick={() => handleStartAssessment()}
                >
                  {formatMessage(messages.startAssessment)}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
        {data.length ? (
          <Typography variant="overline">
            {formatMessage(messages.select)}
          </Typography>
        ) : null}
        <Card>
          <ContentLoader loading={loading}>
            {data.length ? (
              <List>
                {data.map(({ date, id, title, utc }, index, arr) => (
                  <ListItemButton
                    key={`assessment-${index}`}
                    divider={index !== arr.length - 1}
                    onClick={() => navigate(`${route}${id}`)}
                  >
                    <ListItemText
                      primary={title}
                      secondary={formatDate({ date, utc })}
                    />
                    {secondaryAction}
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <NullStateDiv>
                {formatMessage(messages.noAssessments)}
              </NullStateDiv>
            )}
          </ContentLoader>
        </Card>
      </Container>
      <Dialog
        onClose={handleCloseAssessment}
        open={isAssessmentOpen}
        maxWidth="sm"
        fullWidth
        sx={{ maxHeight: Breakpoint.SM }}
        PaperProps={{
          sx: {
            minWidth: 'inherit',
            height: 'fit-content',
            // maxHeight: 450,
            maxWidth: 450,
          },
        }}
      >
        {activePatientData.patient_id &&
          activePatientData.vitall_patient_id && (
            <PatientDialog
              name={formatName(
                activePatientData.first_name,
                activePatientData.last_name
              )}
              onClose={handleCloseAssessment}
              patient_id={activePatientData.patient_id}
              vitall_patient_id={activePatientData.vitall_patient_id}
            />
          )}
      </Dialog>
    </>
  );
}

export default withErrorBoundary(AssessmentsMain, {
  FallbackComponent: ErrorBoundaryFallback,
});
